/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type ReactElement, type ReactNode, useMemo, useState, Suspense } from 'react';
import type { ShopperProducts } from '@/scapi';
import { Await } from 'react-router';
import { cn } from '@/lib/utils';
import ProductQuantityPicker from '@/components/product-quantity-picker';
import { SwatchGroup, Swatch } from '@/components/swatch-group';
import { useVariationAttributes } from '@/hooks/product/use-variation-attributes';
import { useOptionalProductView } from '@/providers/product-view';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { toImageUrl } from '@/lib/images/dynamic-image';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import ProductPrice from '../product-price';
import PayUAffordabilityWidget from '@/components/payu-affordability-widget';
import { isProductSet, isProductBundle } from '@/lib/product/product-utils';
import InventoryMessage, { InventoryStatus } from '../inventory-message';
// @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
import { ProductRatingSummary } from './product-rating-summary';
// @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS
import { useCurrentVariant } from '@/hooks/product/use-current-variant';
import { useTranslation } from 'react-i18next';
import { WishlistButton } from '@/components/buttons/wishlist-button';
import { ShareButton } from '@/components/buttons/share-button';
import { UITarget } from '@/targets/ui-target';
// @sfdc-extension-line SFDC_EXT_BOPIS
import DeliveryOptions from '@/extensions/bopis/components/delivery-options/delivery-options';
//DUMMY DATA CODE Start
import { parseProductSizeGuide } from './size-chart/size-guide-types';
//DUMMY DAT CODE END

type ProductInfoBaseProps = {
    product: ShopperProducts.schemas['Product'];
    hideVariantSelection?: boolean;
    /** Layout style: 'full' (default) shows title, description, inventory; 'compact' shows brand, smaller title, sorted attributes */
    variantStyle?: 'full' | 'compact';
    /** When true and mode is 'edit', show quantity picker (e.g. in cart edit modal) */
    showQuantityInEditMode?: boolean;
    /** Optional current variant from parent orchestration (used by controlled modal flows) */
    currentVariantOverride?: ShopperProducts.schemas['Variant'];
    /** Whether selected variant inventory is currently being fetched */
    isVariantInventoryLoading?: boolean;
    /** Hide top-right action icons (wishlist/share) */
    hideActionIcons?: boolean;
    /** Optional action content rendered inline with title in full variant style */
    headerAction?: ReactNode;
    isQuickAdd?: boolean;
    sizeGuide?: SizeGuide;
    // @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
    /** Disable rating summary interactions (hover popover and review links) */
    disableRatingInteraction?: boolean;
    // @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS
};
type ProductInfoUncontrolledProps = ProductInfoBaseProps & {
    /** Mode for swatch interaction: 'uncontrolled' uses URL navigation */
    swatchMode?: 'uncontrolled';
    onAttributeChange?: never;
    variationValues?: never;
};
type ProductInfoControlledProps = ProductInfoBaseProps & {
    /** Mode for swatch interaction: 'controlled' uses callback */
    swatchMode: 'controlled';
    /** Callback when variant attribute changes in controlled mode */
    onAttributeChange: (attributeId: string, value: string) => void;
    /** Controlled variation values for controlled mode (e.g., {color: 'red', size: 'M'}) */
    variationValues: { [key: string]: string };
};
type ProductInfoProps = ProductInfoUncontrolledProps | ProductInfoControlledProps;

const isControlledVariantValueOrderable = ({
    variants,
    currentSelection,
    attributeId,
    attributeValue,
}: {
    variants: ShopperProducts.schemas['Variant'][] | undefined;
    currentSelection: Record<string, string>;
    attributeId: string;
    attributeValue: string;
}): boolean => {
    if (!variants || variants.length === 0) {
        return true;
    }

    const nextSelection = {
        ...currentSelection,
        [attributeId]: attributeValue,
    };

    return variants
        .filter((variant) =>
            Object.entries(nextSelection).every(([key, value]) => variant.variationValues?.[key] === value)
        )
        .some((variant) => variant.orderable);
};

/**
 * ProductInfo component displays product details including title, description, price, variants, and quantity picker
 *
 * Supports two swatch modes:
 * - uncontrolled mode (default): Swatches use URL navigation for variant selection
 * - controlled mode: Swatches use callbacks for controlled variant selection (used in modals)
 *
 * @param props - Component props
 * @param props.product - The product data to display
 * @param props.swatchMode - Swatch interaction mode ('uncontrolled' or 'controlled')
 * @param props.onAttributeChange - Callback for controlled mode variant changes
 * @param props.variationValues - Controlled variation values for controlled mode
 * @returns JSX element with product information display
 */
export default function ProductInfo({
    product,
    swatchMode = 'uncontrolled',
    onAttributeChange,
    variationValues,
    hideVariantSelection = false,
    variantStyle = 'full',
    showQuantityInEditMode = false,
    currentVariantOverride,
    isVariantInventoryLoading = false,
    hideActionIcons = false,
    headerAction,
    // @sfdc-extension-line SFDC_EXT_RATINGS_REVIEWS
    disableRatingInteraction = false,
    category,
    isQuickAdd = false,
    sizeGuide: explicitSizeGuide,
}: ProductInfoProps): ReactElement {
    const config = useConfig();
    const isProductASet = isProductSet(product);
    const isProductABundle = isProductBundle(product);
    // Use variation attributes hook for URL-aware swatches
    const variationAttributes = useVariationAttributes({ product });
    const urlCurrentVariant = useCurrentVariant({ product });
    const controlledCurrentVariant = useMemo(() => {
        if (swatchMode !== 'controlled') return undefined;
        if (!variationValues) return undefined;

        const potentialVariants =
            product.variants?.filter((variant) =>
                Object.keys(variationValues).every((key) => variant.variationValues?.[key] === variationValues[key])
            ) ?? [];
        return potentialVariants.length === 1 ? potentialVariants[0] : undefined;
    }, [swatchMode, product.variants, variationValues]);
    // For controlled modal flows, prefer explicit override (can include fetched inventory),
    // then controlled selection, then URL-based variant as fallback.
    const currentVariant = currentVariantOverride || controlledCurrentVariant || urlCurrentVariant;
    const productForPrice = useMemo(() => {
        if (!currentVariant) return product;
        // Build a variant-like product shape so ProductPrice does not treat it as master range pricing.
        return {
            ...product,
            ...currentVariant,
            type: { ...(product.type ?? {}), master: false, variant: true },
            variants: undefined,
        } as ShopperProducts.schemas['Product'];
    }, [product, currentVariant]);
    const productForDeliveryOptions = useMemo(() => {
        if (!currentVariant) return product;
        const variantWithInventory = currentVariant as ShopperProducts.schemas['Variant'] & {
            inventory?: ShopperProducts.schemas['Inventory'];
            inventories?: ShopperProducts.schemas['Inventory'][];
        };
        // Preserve the master id for pickup-context bookkeeping while hydrating
        // delivery checks with selected variant inventory.
        return {
            ...product,
            inventory: variantWithInventory.inventory ?? product.inventory,
            inventories: variantWithInventory.inventories ?? product.inventories,
        };
    }, [product, currentVariant]);
    // Get currency from context (automatically derived from locale)
    const { currency } = useSite();
    const productView = useOptionalProductView();
    const [standaloneQuantity, setStandaloneQuantity] = useState(1);
    const [hasUserSelectedQuantity, setHasUserSelectedQuantity] = useState(false);
    const quantity = productView?.quantity ?? standaloneQuantity;
    const isOutOfStock = productView?.isOutOfStock ?? product.inventory?.orderable === false;
    const stockLevel = productView?.stockLevel ?? product.inventory?.ats;
    const maxQuantity = productView?.maxQuantity;
    const setQuantity = productView?.setQuantity ?? setStandaloneQuantity;
    const mode = productView?.mode ?? 'add';
    // @sfdc-extension-line SFDC_EXT_BOPIS
    const basketPickupStore = productView?.basketPickupStore;

    const { t } = useTranslation(['product', 'quantitySelector']);

    const isCompactStyle = variantStyle === 'compact';
    const showQuantity = !isProductASet && !isProductABundle && (mode !== 'edit' || showQuantityInEditMode);

    // Prefer explicit sizeGuide resolved from category template in loader.
    // Falls back to parseProductSizeGuide if no explicit guide is provided.
    const sizeGuide = useMemo(
        () => explicitSizeGuide ?? parseProductSizeGuide(product as any),
        [explicitSizeGuide, product]
    );

    // Sort variation attributes by priority order (size first, then color)
    const ATTRIBUTE_ORDER = ['size', 'color'];
    const sortedVariationAttributes = [...variationAttributes].sort((a, b) => {
        const aIndex = ATTRIBUTE_ORDER.indexOf(a.id);
        const bIndex = ATTRIBUTE_ORDER.indexOf(b.id);
        // Attributes not in the list sort to the end, preserving original order
        const aPriority = aIndex === -1 ? ATTRIBUTE_ORDER.length : aIndex;
        const bPriority = bIndex === -1 ? ATTRIBUTE_ORDER.length : bIndex;
        return aPriority - bPriority;
    });
    const selectedVariationValues = useMemo(() => {
        if (swatchMode === 'controlled') {
            return variationValues ?? {};
        }
        return variationAttributes.reduce<Record<string, string>>((acc, attribute) => {
            const selectedValue = attribute.selectedValue?.value;
            if (selectedValue) {
                acc[attribute.id] = selectedValue;
            }
            return acc;
        }, {});
    }, [swatchMode, variationValues, variationAttributes]);
    const shouldHideInventoryForPartialVariantSelection = useMemo(() => {
        const variants = product.variants ?? [];
        const variationAttributeCount = product.variationAttributes?.length ?? 0;
        if (variants.length === 0 || variationAttributeCount <= 1) {
            return false;
        }

        const potentialVariants = variants.filter((variant) =>
            Object.entries(selectedVariationValues).every(([key, value]) => variant.variationValues?.[key] === value)
        );

        // Keep inventory hidden until the shopper narrows selection down to a single variant.
        return potentialVariants.length !== 1;
    }, [product.variants, product.variationAttributes?.length, selectedVariationValues]);
    const inventoryStatusOverride = useMemo(() => {
        if (!isVariantInventoryLoading && !shouldHideInventoryForPartialVariantSelection) {
            return undefined;
        }
        return (
            inventoryProduct: ShopperProducts.schemas['Product'],
            inventoryVariant?: ShopperProducts.schemas['Variant'] | null
        ) => {
            if (shouldHideInventoryForPartialVariantSelection) {
                return InventoryStatus.UNKNOWN;
            }
            const hasVariants = (inventoryProduct.variants?.length ?? 0) > 0;
            const missingVariantInventory =
                (
                    inventoryVariant as
                    | (ShopperProducts.schemas['Variant'] & {
                        inventory?: ShopperProducts.schemas['Inventory'];
                    })
                    | null
                )?.inventory == null;
            // During quick-add variant fetch, hide transient inventory message
            // until selected variant inventory is available.
            if (hasVariants && missingVariantInventory) {
                return InventoryStatus.UNKNOWN;
            }
            return InventoryStatus.IN_STOCK;
        };
    }, [isVariantInventoryLoading, shouldHideInventoryForPartialVariantSelection]);

    return (
        <div className="relative  ">
            {/* Action icons — top-right */}
            {/* {!isCompactStyle && !hideActionIcons && (
                <div className="absolute top-0 right-0 flex items-center gap-2 z-10">
                    <WishlistButton
                        product={{
                            productId: product.id,
                            productName: product.name,
                            price: product.price,
                            image: product.imageGroups?.[0]?.images?.[0],
                        }}
                        surface="pdp"
                        size="sm"
                        className="!static border border-border bg-background/90 shadow-none hover:border-muted-foreground/50 hover:bg-background"
                    />
                    <ShareButton
                        product={product}
                        size="sm"
                        className="!static border border-border bg-background/90 shadow-none hover:bg-background hover:border-muted-foreground/50 [&_svg]:stroke-[2]"
                    />
                </div>
            )} */}

            {/* Compact style: brand (uppercase) then product name */}
            {isCompactStyle && (
                <>
                    {product.brand && (
                        <p className="text-xs font-normal leading-none uppercase tracking-wide text-secondary-foreground">
                            {product.brand}
                        </p>
                    )}
                    <h2 className="text-3xl font-bold text-card-foreground tracking-tight">{product.name}</h2>
                </>
            )}

            {/* Product Title, SKU, Description */}
            {!isCompactStyle && (
                <div className="flex items-start justify-between gap-4">
                    <div className={`${hideActionIcons ? '' : 'pr-20'} min-w-0`}>
                        {/* {product.brand && (
                            <p className="mb-1 text-xs font-normal leading-none uppercase tracking-wide text-secondary-foreground">
                                {product.brand}
                            </p>
                        )} */}
                        {category && !isQuickAdd && (
                            <Suspense fallback={null}>
                                <Await resolve={category} errorElement={null}>
                                    {(resolved) => {
                                        const name = resolved?.parentCategoryTree?.[0]?.name ?? resolved?.name;
                                        return name ? <p className="pdp-category-label tracking-normal [word-spacing:normal]">{name}</p> : null;
                                    }}
                                </Await>
                            </Suspense>
                        )}
                        <h1
                            data-testid="product-title"
                            className={cn(
                                'tracking-tight',
                                isQuickAdd
                                    ? 'quick-add-title font-lato font-normal text-[20px] leading-[24px] tracking-normal text-[#000000] mb-2'
                                    : '!font-lato text-[24px] !text-black text-card-foreground'
                            )}>
                            {product.name}
                        </h1>
                        {/* {product.id && (
                            <p className="mt-2 text-xs leading-none text-secondary-foreground">
                                {t('sku')} {product.id}
                            </p>
                        )} */}
                        {product.shortDescription && !isQuickAdd && (
                            <p className="mt-2 text-base font-normal leading-6 text-accent-foreground">
                                {product.shortDescription}
                            </p>
                        )}
                    </div>
                    {headerAction ? <div className="pt-1 shrink-0">{headerAction}</div> : null}
                </div>
            )}
            {/* Rating summary - visible on both mobile and desktop */}
            {/* @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS */}
            {/* rating part comment for now */}
            {/* {!isCompactStyle && (
                <UITarget targetId="sfcc.pdp.reviews.rating">
                    <ProductRatingSummary interactive={!disableRatingInteraction} />
                </UITarget>
            )} */}
            {/* @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS */}

            {/* Price - show unit price on PDP */}
            <div className={cn('space-y-3', isQuickAdd && 'space-y-0 mb-4')}>
                <ProductPrice
                    type="unit"
                    product={productForPrice}
                    quantity={quantity}
                    currency={currency}
                    labelForA11y={product?.name}
                    currentPriceProps={{
                        className: `text-2xl font-bold text-price-primary leading-[120%] tracking-[-0.6px] ${isQuickAdd ? 'quick-add-price' : ''}`,
                    }}
                    listPriceProps={{
                        className: `pdp-list-price ${isQuickAdd ? 'quick-add-list-price' : ''}`,
                    }}
                    discountClassName={`pdp-discount ${isQuickAdd ? 'quick-add-discount' : ''}`}
                    promoCalloutProps={{
                        className: 'text-sm [&_span]:mx-0 [&_span]:text-status-positive',
                    }}
                    hidePromo={isCompactStyle}
                    currentPriceOnly={isCompactStyle}
                    showDiscount={true}
                />
            </div>

            {!isCompactStyle && !isQuickAdd && (
                <>
                    <p className="pdp-tax-info">inclusive of all taxes</p>
                    <div className="pdp-tax-divider" />
                </>
            )}



            {/* Inventory Status Message - hidden in compact/edit mode */}
            {/* pay u section commented for now */}
            {/* {!isCompactStyle && (
                <UITarget targetId="sfcc.pdp.shipping.deliveryEstimate">
                    <InventoryMessage
                        product={product}
                        currentVariant={currentVariant}
                        lowStockThreshold={config.global.inventory.lowStockThreshold}
                        getInventoryStatus={inventoryStatusOverride}
                    />
                </UITarget>
            )} */}
            {!isCompactStyle && <UITarget targetId="sfcc.pdp.loyalty.points" />}

            {/* Swatch Groups for Product Variations */}
            {sortedVariationAttributes.map(({ id, name, selectedValue, values }) => {
                // In controlled mode, derive display name from variationValues state
                const controlledValue = variationValues?.[id];
                const controlledDisplayName = controlledValue
                    ? values.find((v) => v.value === controlledValue)?.name || ''
                    : '';

                // When hideVariantSelection is true, only show the selected swatch (read-only)
                const swatchesToShow = hideVariantSelection
                    ? values.filter((v) => v.value === selectedValue?.value)
                    : values;

                const swatches = swatchesToShow.map((value) => {
                    const { href, name: valueName, image, value: swatchValue, orderable } = value;
                    const isOrderableInCurrentSelection =
                        swatchMode === 'controlled'
                            ? isControlledVariantValueOrderable({
                                variants: product.variants,
                                currentSelection: variationValues ?? {},
                                attributeId: id,
                                attributeValue: swatchValue,
                            })
                            : (orderable ?? true);
                    const isSelected = swatchMode === 'controlled'
                        ? variationValues?.[id] === swatchValue
                        : selectedValue?.value === swatchValue;
                    const swatchImageUrl = (image && toImageUrl({ image, config })) || '';

                    const content =
                        id === 'color' ? (
                            <span
                                className="relative block overflow-hidden shrink-0 w-[var(--swatch-inner-dot-size,38px)] h-[var(--swatch-inner-dot-size,38px)] max-md:w-[32px] max-md:h-[32px] rounded-[var(--swatch-inner-radius,4px)]
                                bg-cover bg-center bg-no-repeat shadow-[inset_0_0_0_1px_rgba(0,0,0,0.08)]"
                                style={{
                                    backgroundColor: valueName?.toLowerCase(),
                                    backgroundImage: swatchImageUrl ? `url(${swatchImageUrl})` : undefined,
                                }}
                                aria-label={image?.alt || valueName}>
                                {swatchImageUrl && (
                                    <img
                                        src={swatchImageUrl}
                                        alt={image?.alt || valueName || ''}
                                        className="w-full h-full object-cover"
                                        loading="eager"
                                    />
                                )}
                            </span>
                        ) : image ? (
                            <span
                                className="rounded-none relative block overflow-hidden shrink-0 w-full h-full bg-cover bg-center bg-no-repeat"
                                style={{
                                    backgroundColor: valueName?.toLowerCase(),
                                    backgroundImage: swatchImageUrl ? `url(${swatchImageUrl})` : undefined,
                                    border: 'var(--swatch-color-dot-border, none)',
                                }}
                                aria-label={image.alt || valueName}
                            />
                        ) : (
                            <span className="text-[14px] font-bold flex items-center gap-1.5 pdp-swatch-value-text text-[#000000]">
                                {valueName}
                                {isSelected && (
                                    <svg
                                        className="w-4 h-4 pdp-swatch-tick"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth={3}
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </span>
                        );

                    return (
                        <Swatch
                            key={swatchValue}
                            href={swatchMode === 'uncontrolled' ? href : undefined}
                            // Disable when not orderable (out of stock)
                            disabled={!isOrderableInCurrentSelection}
                            value={swatchValue}
                            name={valueName}
                            shape={id === 'color' ? 'color' : 'label'}
                            className={cn(
                                id === 'color' && 'pdp-color-swatch',
                                id === 'size' && (isQuickAdd ? 'quick-add-size-swatch' : 'pdp-size-swatch'),
                                id === 'size' && 'max-md:!w-[63px] max-md:!min-w-[63px] max-md:!max-w-[63px] max-md:!h-[32px] max-md:!min-h-[32px] max-md:!max-h-[32px] max-md:!p-0'
                            )}
                            labeled={id !== 'color'}>
                            {content}
                        </Swatch>
                    );
                });
                return (
                    <div key={id} className={cn(isQuickAdd ? (id === 'size' ? 'mb-0' : 'mb-4') : 'm-0')}>
                        <SwatchGroup
                            key={id}
                            value={swatchMode === 'uncontrolled' ? selectedValue?.value : controlledValue}
                            displayName={id === 'size' ? '' : (swatchMode === 'controlled' ? controlledDisplayName : selectedValue?.name || '')}
                            label={id === 'size' ? 'Select Size' : id === 'color' ? 'Select Color' : name}
                            ariaLabel={id === 'color' ? 'Color' : undefined}
                            className={cn('pdp-variant-group',isQuickAdd ? (id === 'color' ? 'quick-add-color' : 'quick-add-size') : undefined)}
                            isQuickAdd={isQuickAdd}
                            handleChange={
                                // Disable handleChange when hideVariantSelection is true
                                hideVariantSelection
                                    ? undefined
                                    : swatchMode === 'controlled'
                                    ? (value) => onAttributeChange?.(id, value)
                                    : undefined
                            }
                            //DUMMY DATA CODE Start
                            sizeGuide={id === 'size' ? sizeGuide ?? undefined : undefined}
                            //DUMMY DAT CODE END
                        >
                            {swatches}
                        </SwatchGroup>
                    </div>
                );
            })}
            {!isCompactStyle && <UITarget targetId="sfcc.pdp.products.visualization" />}

            {/* @sfdc-extension-block-start SFDC_EXT_BOPIS */}
            {/* Delivery Options - For individual products */}
            {/* Hide for non-pickup items when opened from cart page */}
            {/* delivery option commented ac to figma */}
            {/* {!isOutOfStock && (mode !== 'edit' || basketPickupStore) && !(isProductABundle || isProductASet) && (
                <DeliveryOptions
                    product={productForDeliveryOptions}
                    quantity={quantity}
                    basketPickupStore={basketPickupStore}
                    className="mt-6"
                />
            )} */}
            {/* @sfdc-extension-block-end SFDC_EXT_BOPIS */}
            {/* {!isCompactStyle && productForPrice.price !== undefined && (
                <PayUAffordabilityWidget amount={productForPrice.price} merchantKey="TWSyp3" />
            )} */}
            {/* Quantity Selector - for non-set/bundle when not edit mode, or when showQuantityInEditMode in edit mode */}
            {showQuantity && (
                <div className="space-y-2">

                    {!isQuickAdd && (
                        <div>
                            <label htmlFor="pdp-quantity-stepper" className="block text-[14px] md:text-[16px] leading-[20px] font-normal text-[#101014] mb-2">
                                Select Quantity :
                            </label>
                            <div className="pdp-quantity-wrapper">
                                <div
                                    id="pdp-quantity-stepper"
                                    className="w-full h-[52px] md:h-[54px] border border-[#000000] rounded-[4px] px-4 flex items-center justify-between select-none bg-white"
                                    data-stock-level={stockLevel}
                                    data-is-out-of-stock={isOutOfStock}
                                    data-product-name={product.name}
                                    data-max-quantity={maxQuantity}
                                    role="group"
                                    aria-label="Quantity selector"
                                >
                                    <button
                                        type="button"
                                        aria-label="Decrease quantity"
                                        disabled={isOutOfStock || quantity <= 1}
                                        onClick={() => {
                                            setHasUserSelectedQuantity(true);
                                            setQuantity(Math.max(1, quantity - 1));
                                        }}
                                        className="w-8 h-8 flex items-center justify-center text-[#101014] disabled:opacity-25 disabled:cursor-not-allowed"
                                    >
                                        <svg className="w-5 h-5 md:w-6 md:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                            <line x1="4" y1="12" x2="20" y2="12" />
                                        </svg>
                                    </button>
                                    <span
                                        aria-live="polite"
                                        data-testid="quantity-value"
                                        className="!font-lato text-[14px] md:text-[16px] !leading-[22px] !font-normal !text-[#101014] text-center select-none"
                                    >
                                        {quantity}
                                    </span>
                                    <button
                                        type="button"
                                        aria-label="Increase quantity"
                                        disabled={isOutOfStock || quantity >= (maxQuantity)}
                                        onClick={() => {
                                            setHasUserSelectedQuantity(true);
                                            setQuantity(quantity + 1);
                                        }}
                                        className="w-8 h-8 flex items-center justify-center text-[#101014] disabled:opacity-25 disabled:cursor-not-allowed"
                                    >
                                        <svg className="w-5 h-5 md:w-6 md:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                            <line x1="12" y1="4" x2="12" y2="20" />
                                            <line x1="4" y1="12" x2="20" y2="12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* Inventory Warning Messages */}
                    {isOutOfStock && (
                        <p className="text-sm font-medium text-destructive mt-1">
                            {t('outOfStock', { productName: product.name })}
                        </p>
                    )}
                    {!isOutOfStock && stockLevel !== undefined && stockLevel > 0 && stockLevel < quantity && (
                        <p className="text-sm font-medium text-destructive mt-1">
                            {isProductABundle || isProductASet
                                ? t('quantitySelector:onlyLeftForProduct', {
                                    stockLevel: stockLevel.toString(),
                                    productName: product.name,
                                    defaultValue: `Only ${stockLevel} left for ${product.name}!`,
                                })
                                : t('quantitySelector:onlyLeft', {
                                    stockLevel: stockLevel.toString(),
                                    defaultValue: `Only ${stockLevel} left!`,
                                })}
                        </p>
                    )}
                </div>
            )}

            {/* Product Bundle/Set Notice */}
            {(isProductASet || isProductABundle) && (
                <div className="bg-primary/10 border border-primary rounded-none p-4">
                    <p className="text-sm text-primary">
                        {isProductASet ? t('productSetNotice') : t('productBundleNotice')}
                    </p>
                </div>
            )}
        </div>
    );
}
